'use client';

import { useEffect, useRef } from 'react';

import {
  notificationsApi,
  useCreateNotificationsRealtimeTicketMutation,
} from '@/features/notifications/api/notificationsApi';
import { NotificationRealtimeClient } from '@/features/notifications/realtime/notificationRealtimeClient';
import { useAppDispatch } from '@/store/hooks';
import { notify } from '@web/utils';

import type {
  NotificationInboxData,
  NotificationInboxItem,
  NotificationRealtimeEvent,
} from '@/features/notifications/types';
import type { APIResponse } from '@web/types';

function patchNotificationInCache(
  draft: APIResponse<NotificationInboxData>,
  notification: NotificationInboxItem,
) {
  if (!draft.success) {
    return;
  }

  const index = draft.data.results.findIndex(
    (item) => item.id === notification.id,
  );
  if (index >= 0) {
    draft.data.results[index] = notification;
  }
}

export function useNotificationsRealtime({
  enabled = true,
}: {
  enabled?: boolean;
} = {}) {
  const dispatch = useAppDispatch();
  const [createTicket] = useCreateNotificationsRealtimeTicketMutation();
  const clientRef = useRef<NotificationRealtimeClient | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    let isDisposed = false;

    const invalidateInbox = () => {
      dispatch(
        notificationsApi.util.invalidateTags([
          { type: 'Notifications', id: 'INBOX' },
        ]),
      );
    };

    const handleEvent = (event: NotificationRealtimeEvent) => {
      if (event.type === 'notification.created') {
        invalidateInbox();
        return;
      }

      if (event.type === 'notification.unread_count_changed') {
        dispatch(
          notificationsApi.util.updateQueryData(
            'getNotificationInbox',
            { page_size: 20 },
            (draft) => {
              if (draft.success) {
                draft.data.unread_count = event.unread_count;
              }
            },
          ),
        );
        return;
      }

      if (
        event.type === 'notification.read' ||
        event.type === 'notification.archived'
      ) {
        dispatch(
          notificationsApi.util.updateQueryData(
            'getNotificationInbox',
            { page_size: 20 },
            (draft) => patchNotificationInCache(draft, event.notification),
          ),
        );
      }
    };

    const connect = async () => {
      try {
        const ticket = await createTicket().unwrap();

        if (isDisposed || !ticket.data) {
          return;
        }

        const client = new NotificationRealtimeClient({
          websocketUrl: ticket.data.websocket_url,
          onEvent: handleEvent,
          onOpen: invalidateInbox,
        });

        clientRef.current = client;
        client.connect();
      } catch {
        if (!isDisposed) {
          notify({
            type: 'error',
            message: 'Unable to connect to notification updates.',
          });
        }
      }
    };

    void connect();

    return () => {
      isDisposed = true;
      clientRef.current?.disconnect();
      clientRef.current = null;
    };
  }, [createTicket, dispatch, enabled]);
}
